import { useNavigate, useLocation } from 'react-router-dom';
import { useContext, useEffect, useRef, useState } from 'react';

import { DataContext } from 'providers/data';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ButtonRectangle } from 'components/views/form/Button';

import { AdditionalServices, Container, ContentButton, ContentCart, ContentPlan, ContentTotal, ContentRemove, RiskWhite, TypographyText } from './styles';

import CloseIcon from '@mui/icons-material/Close';

type LocationState = {
    isExchangesInProgress?: boolean;
    cD_PEDIDO?: number;
}

const ShoppingCart = ({ isLoading = false, isWebView = false }: { isLoading?: boolean, isWebView?: boolean }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { pathname } = location;
    const locationState = location.state as LocationState | undefined;

    const { dataProductCart, dataGetListarCotacoes, selectedCar, handleParseProductCart, handlePostChooseQuoteItems, handleClearDataValidateChoose } = useContext(DataContext);

    const refOpen = useRef<HTMLDivElement>(null);
    const refScroll = useRef<HTMLDivElement>(null);
    const refAddService = useRef<HTMLLIElement>(null);

    const [isShow, setIsShow] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isOpenAnimation, setIsOpenAnimation] = useState(false);

    const handleChangePlanClick = () => {
        if (isLoading) return;
        handleClearDataValidateChoose();
        const basePath = isWebView ? '/wb' : '';
        navigate(`${basePath}/central-de-trocas/troca-de-servico/planos`);
    };

    useEffect(() => {
        const handleScroll = () => {
            if (refScroll.current && window.innerWidth > 992 && window.scrollY < (document.body.clientHeight - 1100)) {
                refScroll.current.style.top = `${window.scrollY + (window.innerHeight > 850 ? 58 : 83)}px`;
            }
        }

        const handleClose = (e: any) => {
            if (refOpen.current?.contains(e.target)) {
                setIsOpen(false);
            }

            if (refScroll.current?.contains(e.target)) {
                setIsOpen(true);
                if (isOpen && !refAddService.current?.contains(e.target)) {
                    setIsOpen(false);
                }
            }
        }

        window.addEventListener('scroll', handleScroll);
        document.body.addEventListener('click', handleClose);

        return () => {
            window.removeEventListener('click', handleScroll);
            document.body.removeEventListener('click', handleClose);
        }
    });

    useEffect(() => {
        setTimeout(() => {
            setIsOpenAnimation(isOpen);
        }, !isOpen ? 400 : 0);
    }, [isOpen]);

    useEffect(() => {
        setTimeout(() => {
            setIsShow(true);
            setIsOpenAnimation(false);
        }, 400);

        setIsShow(false);
    }, []);

    useEffect(() => {
        if (!locationState?.isExchangesInProgress) {
            return;
        }

        if (!dataProductCart && dataGetListarCotacoes?.quoteItemsChooses && selectedCar) {
            handleParseProductCart({
                body: {
                    customerId: selectedCar.CD_PESSOA,
                    cD_PEDIDO: locationState?.cD_PEDIDO ?? dataGetListarCotacoes.cD_PEDIDO,
                    cD_COTACAO: dataGetListarCotacoes.quote.cD_COTACAO,
                    quoteItemsChooses: dataGetListarCotacoes.quoteItemsChooses
                }
            });
        }
    }, [locationState?.isExchangesInProgress, locationState?.cD_PEDIDO, dataGetListarCotacoes, selectedCar, handleParseProductCart]);

    return (
        <ContentCart
            ref={refOpen}
            className={isOpen ? 'open' : 'close'}
            primaryshow={isShow ? 'active' : 'disabled'}
            isWebView={isWebView ? 'active' : 'disabled'}
        >
            <Container ref={refScroll} className={isOpenAnimation ? 'open' : 'close'}>
                <RiskWhite />
                <Typography variant='h5' color='white' fontWeight={700} marginBottom='1.5rem' textTransform='uppercase'>
                    Resumo do seu pedido
                </Typography>
                
                <ContentPlan>
                    <nav>
                        <Typography variant='h6' padding='6px 6px 0' fontWeight={700} width='fit-content' textTransform='capitalize'>
                            Seu plano mensal
                        </Typography>

                        {!pathname?.includes('agendamento') &&
                            <button
                                disabled={isLoading}
                                onClick={handleChangePlanClick}>
                                Alterar
                            </button>
                        }
                    </nav>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <TypographyText variant='subtitle2' textTransform='capitalize'>
                            {dataProductCart.productPlan.nM_PRODUTO}
                        </TypographyText>


                        <TypographyText variant='subtitle2'>
                            {dataProductCart.productPlan.vL_TOTAL}
                        </TypographyText>
                    </Box>

                    <Typography variant='subtitle2' textTransform='capitalize' sx={{ color: 'white' }}>
                        {dataProductCart.productPlan.nR_PORCENTAGEM_INDENIZACAO > 0 &&
                            <span style={{ fontSize: '.75rem', fontWeight: '400', lineHeight: '1.5rem' }}>
                                {dataProductCart.productPlan.nR_PORCENTAGEM_INDENIZACAO}% DA FIPE
                            </span>
                        }
                        {dataProductCart?.productPlan.dS_PRODUTO ?
                            <span style={{ display: 'flex', fontSize: '.75rem', fontWeight: '400' }}>
                                {dataProductCart?.productPlan.dS_PRODUTO}
                            </span>
                            :
                            <div></div>
                        }
                    </Typography>

                    <nav>
                        <Typography fontSize='.93rem' color='white' width='fit-content' marginTop='1rem' lineHeight='.7rem' textTransform='capitalize'>
                            Serviços adicionais
                        </Typography>
                        {pathname?.includes('pagamento') &&
                            <button
                                disabled={isLoading}
                                onClick={() => !isLoading && navigate(`${isWebView ? '/wb' : ''}/central-de-trocas/troca-de-servico/servicos-adicionais`)}>
                                Adicionar
                            </button>
                        }
                    </nav>

                    <AdditionalServices>
                        {dataProductCart.productAdd.map(({ nM_PRODUTO, vL_TOTAL }, index) => (
                            <li ref={refAddService} key={index}>
                                <TypographyText variant='subtitle2'>
                                    {nM_PRODUTO}
                                </TypographyText>

                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    {((pathname.split('/')[3] === 'pagamento') || (pathname === 'agendamento')) &&
                                        <ContentRemove disabled={isLoading} onClick={() => { }}>
                                            <CloseIcon />
                                        </ContentRemove>
                                    }

                                    <TypographyText variant='subtitle2'>
                                        {vL_TOTAL}
                                    </TypographyText>
                                </Box>
                            </li>
                        ))}
                    </AdditionalServices>
                </ContentPlan>

                <ContentTotal>
                    <nav>
                        <Typography
                            padding='6px 6px 0'
                            fontWeight={700}
                            width='fit-content'
                            sx={{
                                fontSize: '1.25rem',
                                borderRadius: '5px 5px 0px 0px',
                                backgroundColor: '#97C93D'
                            }}>
                            TOTAL
                        </Typography>
                    </nav>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
                        <TypographyText variant='subtitle2' lineHeight='23px' textTransform="uppercase">
                            mensalidades
                        </TypographyText>

                        <TypographyText variant='h5'>
                            <span style={{ fontSize: '.75rem', fontWeight: '400' }}>R$</span>
                            {parseFloat(`${dataProductCart.vL_TOTAL_Cart}`.replace(',', '.')).toLocaleString('pt-br')}
                        </TypographyText>
                    </Box>

                    {(pathname.includes('servicos-adicionais')) &&
                        <ContentButton>
                            <ButtonRectangle name='CONTINUAR' onClick={() => { handlePostChooseQuoteItems(); navigate(`${isWebView ? '/wb' : ''}/central-de-trocas/troca-de-servico/pagamento`); }} />
                        </ContentButton>
                    }
                </ContentTotal>
            </Container>
        </ContentCart >
    )
}

export default ShoppingCart;